import { useEffect, useMemo, useState } from 'react'

interface UserActionOption {
  id: string
  label: string
  description?: string
}

interface UserActionInput {
  question: string
  selectionMode: 'single' | 'multiple'
  options: UserActionOption[]
  allowFreeText: boolean
}

interface ToolUserActionFormProps {
  callId: string
  input?: Record<string, unknown>
  isSubmitting: boolean
  onSubmit: (userResponse: string) => Promise<void>
}

const parseUserActionInput = (input?: Record<string, unknown>): UserActionInput | null => {
  if (!input || typeof input !== 'object' || Array.isArray(input)) return null

  const question = input.question
  const selectionMode = input.selectionMode
  const options = input.options
  const allowFreeText = input.allowFreeText

  if (typeof question !== 'string' || question.trim().length < 1) return null
  if (selectionMode !== 'single' && selectionMode !== 'multiple') return null
  if (!Array.isArray(options) || options.length < 2 || options.length > 6) return null

  const parsedOptions: UserActionOption[] = options
    .map((option) => {
      if (!option || typeof option !== 'object' || Array.isArray(option)) return null
      const id = (option as Record<string, unknown>).id
      const label = (option as Record<string, unknown>).label
      const description = (option as Record<string, unknown>).description
      if (typeof id !== 'string' || id.trim().length < 1) return null
      if (typeof label !== 'string' || label.trim().length < 1) return null
      if (description !== undefined && typeof description !== 'string') return null
      return { id, label, ...(typeof description === 'string' ? { description } : {}) }
    })
    .filter((option): option is UserActionOption => option !== null)

  if (parsedOptions.length !== options.length) return null

  return {
    question,
    selectionMode,
    options: parsedOptions,
    allowFreeText: typeof allowFreeText === 'boolean' ? allowFreeText : false
  }
}

export const ToolUserActionForm = ({ callId, input, isSubmitting, onSubmit }: ToolUserActionFormProps) => {
  const [selectedOptionIds, setSelectedOptionIds] = useState<string[]>([])
  const [freeTextResponse, setFreeTextResponse] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)

  const userActionInput = useMemo(() => parseUserActionInput(input), [input])

  useEffect(() => {
    setSelectedOptionIds([])
    setFreeTextResponse('')
    setError(undefined)
  }, [callId, input])

  const toggleOption = (optionId: string) => {
    if (!userActionInput) return

    setError(undefined)
    if (userActionInput.selectionMode === 'single') {
      setSelectedOptionIds([optionId])
      return
    }

    setSelectedOptionIds((previous) => (
      previous.includes(optionId)
        ? previous.filter((id) => id !== optionId)
        : [...previous, optionId]
    ))
  }

  const handleSubmit = async () => {
    if (!userActionInput) return

    const trimmedFreeText = freeTextResponse.trim()
    const hasSelectedOptions = selectedOptionIds.length > 0
    const hasFreeText = userActionInput.allowFreeText && trimmedFreeText.length > 0

    if (!hasSelectedOptions && !hasFreeText) {
      setError(
        userActionInput.allowFreeText
          ? 'Select at least one option or provide a custom response.'
          : 'Select at least one option before submitting.'
      )
      return
    }

    const selectedOptions = userActionInput.options
      .filter((option) => selectedOptionIds.includes(option.id))
      .map(({ id, label, description }) => ({ id, label, ...(description ? { description } : {}) }))

    const responsePayload = {
      question: userActionInput.question,
      selectionMode: userActionInput.selectionMode,
      selectedOptionIds,
      selectedOptions,
      ...(trimmedFreeText ? { freeText: trimmedFreeText } : {})
    }

    await onSubmit(JSON.stringify(responsePayload))
  }

  if (!userActionInput) {
    return (
      <div className='mp-agent-tool-user-action-form'>
        <div className='mp-agent-tool-input-error'>Invalid user action input schema in tool call.</div>
      </div>
    )
  }

  return (
    <div className='mp-agent-tool-user-action-form'>
      <div className='mp-agent-tool-user-action-question'>{userActionInput.question}</div>
      <div className='mp-agent-tool-user-action-options'>
        {userActionInput.options.map((option) => {
          const optionInputId = `${callId}-${option.id}`
          const checked = selectedOptionIds.includes(option.id)
          return (
            <label key={option.id} htmlFor={optionInputId} className='mp-agent-tool-user-action-option'>
              <input
                id={optionInputId}
                type={userActionInput.selectionMode === 'single' ? 'radio' : 'checkbox'}
                name={`mp-agent-tool-user-action-${callId}`}
                checked={checked}
                onChange={() => toggleOption(option.id)}
                disabled={isSubmitting}
              />
              <span className='mp-agent-tool-user-action-option-content'>
                <span className='mp-agent-tool-user-action-option-label'>{option.label}</span>
                {option.description ? <span className='mp-agent-tool-user-action-option-description'>{option.description}</span> : null}
              </span>
            </label>
          )
        })}
      </div>

      {userActionInput.allowFreeText ? (
        <textarea
          className='mp-agent-tool-user-action-free-text'
          value={freeTextResponse}
          onChange={(event) => {
            setFreeTextResponse(event.target.value)
            if (error) setError(undefined)
          }}
          placeholder='Optional custom response'
          rows={3}
          disabled={isSubmitting}
        />
      ) : null}

      {error ? <div className='mp-agent-tool-input-error'>{error}</div> : null}

      <div className='mp-agent-tool-approval-actions'>
        <button className='mp-agent-tool-approve-button' onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit response'}
        </button>
      </div>
    </div>
  )
}
